import { unix_utc_to_local_12_hour_clock } from "../../../utils/date_time_utils";
import { useTranslation as UseTranslation } from "react-i18next";
import { formatLatLon } from "../../../utils/lat_lon_utils";

export default function columns(props) {
  const {
    facilities = {},
    onClick = () => { },
    timeZone = '',
    useTranslation = true,
    zones = {},
  } = props;
  const { t = () => { } } = useTranslation ? UseTranslation() : () => { };

  const historyTableColumns = [
    // timeOfLog
    {
      field: "timeOfLog",
      minWidth: 180,
      flex: 1,
      groupable: false,
      headerClassName: "datagrid-header",
      headerName: "Date/Time",
      id: "timeOfLog",
      renderCell: (data) => {
        const { row } = data;
        const { timeOfLogLong } = row;

        return (
          <div>
            {timeOfLogLong
              ? unix_utc_to_local_12_hour_clock(timeOfLogLong, timeZone)
              : t("dnpinput1")}
          </div>
        );
      },
      type: "dateTime",
      valueGetter: (value, row) => {
        const timeOfLogLong = row?.timeOfLogLong;
        return timeOfLogLong
          ? new Date(timeOfLogLong)
          : //furthest date in the future. We want records without a timeOfLogLong to be at the bottom of the table in case of "newest to oldest" sort
          new Date(8640000000000000);
      },
    },
    // tag
    {
      field: "tag",
      minWidth: 120,
      flex: 1,
      headerClassName: "datagrid-header",
      headerName: "Tag",
      id: "tag",
      renderCell: (data) => {
        if (data.rowNode.type === "group") return data.value;
        const { row } = data;
        const { tag, assetTag } = row;
        return (
          <div>
            {tag ? tag : assetTag ? assetTag : t("dnpinput1")}
          </div>
        );
      },
      valueGetter: (value, row) => {
        const { tag, assetTag } = row;
        return tag ? tag : assetTag ? assetTag : t("dnpinput1");
      },
      groupingValueGetter: (value, row) => {
        const { tag, assetTag } = row;
        return tag ? tag : assetTag ? assetTag : null;
      },
    },
    // latLong
    {
      field: "latLong",
      minWidth: 150,
      flex: 1,
      headerName: "Latitude, Longitude",
      id: "latLong",
      renderCell: (data) => {
        if (data.rowNode.type === "group") return data.value;
        const { row } = data;
        const { latitude, longitude } = row;

        const formattedLatLong =
          latitude && longitude
            ? formatLatLon(latitude, longitude)
            : t("dnpinput1");

        return (
          <div>
            {formattedLatLong}
          </div>
        );
      },
      valueGetter: (value, row) => {
        const { latitude, longitude } = row;

        const formattedLatLong =
          latitude && longitude
            ? formatLatLon(latitude, longitude)
            : t("dnpinput1");

        return formattedLatLong;
      },
      groupingValueGetter: (value, row) => {
        const { latitude, longitude } = row;

        const formattedLatLong =
          latitude && longitude
            ? formatLatLon(latitude, longitude)
            : t("dnpinput1");

        return formattedLatLong || null;
      },
    },
    // facility
    {
      field: "facility",
      minWidth: 120,
      headerClassName: "datagrid-header",
      headerName: "Facility Name",
      id: "facility",
      renderCell: (data) => {
        if (data.rowNode.type === "group") return data.value;
        const { row } = data;
        const { facility, latitude, longitude } = row;
        // Only show facility if the event was actually at that facility (has facility name)
        // If the event has GPS coordinates, it's outside - show "-"
        const hasGpsCoordinates = latitude && longitude && (latitude !== 0 || longitude !== 0);
        return (
          <div>
            {facility
              ? facility
              : !latitude && !longitude
                ? t("dnpinput1")
                : hasGpsCoordinates
                  ? "-"
                  : "Not Registered"}
          </div>
        );
      },
      valueGetter: (value, row) => {
        const { facility, latitude, longitude } = row;
        const hasGpsCoordinates = latitude && longitude && (latitude !== 0 || longitude !== 0);
        return facility
          ? facility
          : !latitude && !longitude
            ? t("dnpinput1")
            : hasGpsCoordinates
              ? "-"
              : "Not Registered";
      },
      groupingValueGetter: (value, row) => {
        const { facility, latitude, longitude } = row;
        const hasGpsCoordinates = latitude && longitude && (latitude !== 0 || longitude !== 0);
        return facility
          ? facility
          : !latitude && !longitude
            ? null
            : hasGpsCoordinates
              ? "-"
              : null;
      },
      flex: 1,
    },
    // state
    {
      field: "state",
      minWidth: 60,
      flex: 0.5,
      headerName: "State",
      id: "state",
      renderCell: (data) => {
        if (data.rowNode.type === "group") return data.value;
        const { row, assetId } = data;
        const { state = t("dnpinput1") } = row;

        return (
          <div>
            {state ? state : t("dnpinput1")}
          </div>
        );
      },
      groupingValueGetter: (value, row) => {
        return row?.state || null;
      },
    },
    // zone
    {
      field: "zone",
      minWidth: 100,
      flex: 1,
      headerClassName: "datagrid-header",
      headerName: "Zone",
      id: "zone",
      renderCell: (data) => {
        if (data.rowNode.type === "group") return data.value;
        const { row } = data;
        const { zone = {} } = row;
        const { zoneName = "", pZoneName = "", zoneId = "" } = zone;

        return (
          <div>
            {/* Show pZoneName. If not present show pZoneName. If not present the Zone Name */}
            {pZoneName
              ? pZoneName
              : zoneName
                ? zoneName
                : zoneId && zones[zoneId]
                  ? zones[zoneId].name
                  : t("dnpinput1")}
          </div>
        );
      },
      valueGetter: (value, row) => {
        const { zone = {} } = row;
        const { zoneName = "", pZoneName = "", zoneId = "" } = zone;

        return zoneName
          ? zoneName
          : pZoneName
            ? pZoneName
            : zoneId && zones[zoneId]
              ? zones[zoneId].name
              : t("dnpinput1");
      },
      groupingValueGetter: (value, row) => {
        const { zone = {} } = row;
        const { zoneName = "", pZoneName = "", zoneId = "" } = zone;

        return zoneName
          ? zoneName
          : pZoneName
            ? pZoneName
            : zoneId && zones[zoneId]
              ? zones[zoneId].name
              : null;
      },
    },
  ]

  return { historyTableColumns };

}